/**
 * useThrottledFrame — runs a callback at a reduced frequency instead of every frame.
 *
 * In VR optimized mode, non-essential callbacks are skipped entirely
 * to maximize GPU rendering time on Quest.
 *
 * Usage:
 *   useThrottledFrame((state, delta) => { ... }, 3); // runs every 3rd frame
 *   useThrottledFrame((state, delta) => { ... }, 3, true); // essential — runs even in VR
 */
import { useRef } from 'react';
import { useFrame, RootState } from '@react-three/fiber';

export function useThrottledFrame(
  callback: (state: RootState, delta: number) => void,
  every: number = 3,
  essential: boolean = false,
) {
  const count = useRef(0);
  useFrame((state, delta) => {
    // In VR optimized mode, skip non-essential throttled callbacks entirely
    if (!essential && (window as any).__vrOptimizedMode) return;
    count.current++;
    if (count.current % every === 0) {
      callback(state, delta);
    }
  });
}
